import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import {
  Advice,
  ClassifyImageParams,
  ClassificationResult,
  ImageClassificationError,
  ImageClassificationRequest,
  RelayEnvironment
} from '@jump-tech-frontend/domain';
import { ActivatedRoute, Params } from '@angular/router';
import { EnvironmentToken } from '@jump-tech-frontend/app-config';

const FAILED_TO_CLASSIFY_MESSAGE = 'Failed to classify image';

const ANALYSIS_FAILED_ADVICE: Advice = { code: 'analysisFailed', severity: 'error', params: {} };

const IMAGE_TOO_LARGE_ADVICE: Advice = { code: 'imageTooLarge', severity: 'error', params: {} };

type ServiceFailure = Pick<ImageClassificationError, 'analysisId' | 'outcome' | 'advice'>;

@Injectable({ providedIn: 'root' })
export class ImageClassificationService {
  private http = inject(HttpClient);
  private activatedRoute = inject(ActivatedRoute);
  private environment = inject<RelayEnvironment>(EnvironmentToken);

  private readonly API_URL = this.environment.apiImageAnalysisUrl;
  private readonly DEFAULT_TIMEOUT = 30000;
  private readonly RETRY_DELAY_MS = 1000;
  private readonly MAX_RETRY_ATTEMPTS = 2;
  private suid: string;

  constructor() {
    this.subscribeToQueryParams();
  }

  /**
   * Classify an image to determine its equipment type.
   * @param params Classification parameters
   * @returns Observable of classification result
   */
  classifyImage(params: ClassifyImageParams): Observable<ClassificationResult> {
    const { base64Image, deviceContext = 'mobile', options, classificationType, projectId, analysisScope } = params;

    // Remove data URL prefix if present
    const cleanBase64 = this.cleanBase64(base64Image);

    // Check base64 size before API call (8MB limit)
    const MAX_BASE64_SIZE_BYTES = 8 * 1024 * 1024;
    if (cleanBase64.length > MAX_BASE64_SIZE_BYTES) {
      return of({
        status: 'error',
        success: false,
        equipmentType: 'Unknown',
        confidence: 0,
        fuseCutoutConfidence: 0,
        consumerUnitConfidence: 0,
        isolatorSwitchConfidence: 0,
        confidenceThresholdMet: false,
        qualityScore: 0,
        qualityAcceptable: false,
        qualityIssues: ['Image exceeds the maximum allowed size of 8MB.'],
        warnings: [],
        error: 'Image is too large to process. Please use a smaller image.',
        errorDetails: ['Image exceeds the maximum allowed size of 8MB.'],
        boundingBoxes: undefined,
        detectionSettings: undefined,
        outcome: 'error',
        advice: [IMAGE_TOO_LARGE_ADVICE]
      });
    }

    // Strict parameters for high-quality image uploads only
    const request: ImageClassificationRequest = {
      image: cleanBase64
    };

    if (classificationType) {
      request.classificationType = classificationType;
    }

    if (projectId) {
      request.projectId = projectId;
    }

    if (analysisScope) {
      request.analysisScope = analysisScope;
    }

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      skip: 'true'
    });

    if (deviceContext === 'mobile') {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `suid ${this.suid}`
      });
    }

    return this.http.post<ClassificationResult>(this.API_URL, request, { headers }).pipe(
      timeout(options?.timeout || this.DEFAULT_TIMEOUT),
      retry({
        count: this.MAX_RETRY_ATTEMPTS,
        delay: (error, retryCount) => {
          if (this.shouldRetryError(error)) {
            return timer(this.RETRY_DELAY_MS * retryCount);
          }

          return throwError(() => error);
        }
      }),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Handle errors from the classification API
   */
  private handleError(error: {
    name?: string;
    status?: number;
    error?: ImageClassificationError;
  }): Observable<ClassificationResult> {
    console.error('Image classification error:', error);

    let errorMessage = FAILED_TO_CLASSIFY_MESSAGE;
    const errorDetails: string[] = [];

    if (error.name === 'TimeoutError') {
      errorMessage = 'Classification request timed out';
      errorDetails.push('The image processing took too long. Try with a smaller image.');
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to classification service';
      errorDetails.push('Please check your network connection.');
    } else if (error.status === 400) {
      errorMessage = 'Invalid image or processing error';
      if (typeof error.error?.detail === 'string' && error.error.detail.includes('too_small')) {
        errorMessage = 'Invalid image (image too small)';
      }
      if (error.error?.error) {
        errorDetails.push(error.error.error);
      }
    } else if (error.status && error.status >= 500) {
      errorMessage = 'Classification service error';
      errorDetails.push('The service is temporarily unavailable.');
    } else if (error.error?.error) {
      errorMessage = error.error.error;
    }

    return of(this.toFailedResult(errorMessage, errorDetails, error.error));
  }

  private toFailedResult(
    errorMessage: string,
    errorDetails: string[],
    failure: ServiceFailure | undefined
  ): ClassificationResult {
    return {
      status: 'error',
      analysisId: failure?.analysisId,
      outcome: failure?.outcome ?? 'error',
      advice: failure?.advice ?? [ANALYSIS_FAILED_ADVICE],
      success: false,
      equipmentType: 'Unknown',
      confidence: 0,
      fuseCutoutConfidence: 0,
      consumerUnitConfidence: 0,
      isolatorSwitchConfidence: 0,
      confidenceThresholdMet: false,
      qualityScore: 0,
      qualityAcceptable: false,
      qualityIssues: errorDetails,
      warnings: [],
      error: errorMessage,
      errorDetails,
      boundingBoxes: undefined,
      detectionSettings: undefined
    };
  }

  private shouldRetryError(error: { name?: string; status?: number }): boolean {
    return error?.status === 0 || error?.name === 'TimeoutError';
  }

  /**
   * Remove data URL prefix from base64 string
   */
  private cleanBase64(base64: string): string {
    if (base64.includes(',')) {
      return base64.split(',')[1];
    }
    return base64;
  }

  private subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.subscribe(async (params: Params) => {
      this.suid = params['suid'] || '';
    });
  }
}
